/**
 * MilestoneService
 *
 * This service handles milestone-related operations for the Project Milestones Experience.
 * It provides methods for listing project milestones and updating milestones inline.
 *
 * @class
 */

import HttpService from './HttpService';
import {
  pathProjectMilestones,
  pathProjectMilestoneId,
} from '@frontend/__settings__/septSoftwarePaths';
import type {
  WorkflowGenericResponse,
  WorkflowMilestoneUpdatePayload,
  WorkflowMilestoneUpdateResponseData,
} from '@frontend/types/workflows';

/**
 * Thrown by {@link MilestoneService.setProjectDueDate} when a project has no
 * dated milestone to anchor the due date to.
 */
export class NoMilestoneError extends Error {
  constructor() {
    super('Project has no milestone to set a due date on.');
    this.name = 'NoMilestoneError';
  }
}

// Audit entry interface
export interface MilestoneAuditEntry {
  auditId: string;
  milestoneId: string;
  fieldName: string;
  oldValue: string | null;
  newValue: string | null;
  actor: string;
  actorId: string | null;
  changeTimestamp: string;
  changeReason: string | null;
}

export interface MilestoneAuditResponseData {
  milestoneId: string;
  projectId: string;
  entries: MilestoneAuditEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Create milestone payload interface
export interface CreateMilestonePayload {
  name: string;
  category?: string;
  discipline?: string;
  role?: string;
  plannedDate?: string;
  dependencies?: string[];
  description?: string;
}

export interface CreateMilestoneResponseData {
  milestoneId: string;
  projectId: string;
  name: string;
  category?: string | null;
  discipline?: string | null;
  roleName?: string | null;
  plannedDate?: string | null;
  status: string;
  notes?: string | null;
  milestoneOrder: number;
  dependencies: string[];
  isAdHoc: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
  createdBy?: string | null;
}

const http = new HttpService();

export interface MilestoneListFilters {
  status?: string[];
  discipline?: string[];
  owner?: string[];
  category?: string[];
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface MilestoneRow {
  milestoneId: string;
  name: string;
  category?: string | null;
  discipline?: string | null;
  plannedDate?: string | null;
  actualDate?: string | null;
  status: string;
  statusBadge: {
    tone: string;
    label: string;
    reason?: string;
  };
  ownerRole?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown>;
  warnings?: string[];
  updatedAt?: string | null;
  createdAt?: string | null;
  isOverdue: boolean;
  actions: {
    canMarkCompleted: boolean;
    canMarkBlocked: boolean;
    canMarkInProgress: boolean;
  };
  // Edit state metadata
  isSaving?: boolean;
  errorMessage?: string | null;
}

export interface MilestoneListResponseData {
  projectId: string;
  filters: {
    status: string[];
    discipline: string[];
    owner: string[];
    category: string[];
    search: string | null;
    sortBy: string;
    sortOrder: string;
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  milestones: MilestoneRow[];
}

class MilestoneService {
  /**
   * Retrieves milestones for a project with optional filters and pagination.
   *
   * @param {string} projectId - ID of the project
   * @param {MilestoneListFilters} filters - Optional filters for status, discipline, owner, category, search, pagination
   * @returns {Promise<WorkflowGenericResponse<MilestoneListResponseData>>} List of milestones with pagination
   * @throws {Error} If the API request fails
   */
  static async getProjectMilestones(
    projectId: string,
    filters?: MilestoneListFilters
  ): Promise<WorkflowGenericResponse<MilestoneListResponseData>> {
    if (!projectId) {
      throw new Error('Project ID is required.');
    }

    const queryParams: Record<string, string | number | string[]> = {};
    
    if (filters) {
      if (filters.status && filters.status.length > 0) {
        queryParams.status = filters.status;
      }
      if (filters.discipline && filters.discipline.length > 0) {
        queryParams.discipline = filters.discipline;
      }
      if (filters.owner && filters.owner.length > 0) {
        queryParams.owner = filters.owner;
      }
      if (filters.category && filters.category.length > 0) {
        queryParams.category = filters.category;
      }
      if (filters.search) {
        queryParams.search = filters.search;
      }
      if (filters.page != null) {
        queryParams.page = filters.page;
      }
      if (filters.limit != null) {
        queryParams.limit = filters.limit;
      }
      if (filters.sortBy) {
        queryParams.sortBy = filters.sortBy;
      }
      if (filters.sortOrder) {
        queryParams.sortOrder = filters.sortOrder;
      }
    }

    return http.get<WorkflowGenericResponse<MilestoneListResponseData>>(
      pathProjectMilestones(projectId),
      {
        params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      }
    );
  }

  /**
   * Updates a milestone inline (status, plannedDate, actualDate, notes).
   * Automatically sets status to 'completed' when actualDate is provided.
   * Returns updated milestone with audit trail of changes.
   *
   * @param {string} projectId - ID of the project
   * @param {string} milestoneId - ID of the milestone to update
   * @param {WorkflowMilestoneUpdatePayload} payload - Update data including status, plannedDate, actualDate, or notes
   * @returns {Promise<WorkflowGenericResponse<WorkflowMilestoneUpdateResponseData>>} Updated milestone with change summary
   * @throws {Error} If the API request fails
   */
  static async updateMilestone(
    projectId: string,
    milestoneId: string,
    payload: WorkflowMilestoneUpdatePayload
  ): Promise<WorkflowGenericResponse<WorkflowMilestoneUpdateResponseData>> {
    if (!projectId) {
      throw new Error('Project ID is required.');
    }
    if (!milestoneId) {
      throw new Error('Milestone ID is required.');
    }

    return http.patch<
      WorkflowGenericResponse<WorkflowMilestoneUpdateResponseData>,
      WorkflowMilestoneUpdatePayload
    >(pathProjectMilestoneId(projectId, milestoneId), payload);
  }

  /**
   * Sets a project's "due date" by updating the planned date of its latest
   * milestone (the one that defines the schedule aggregate `lastPlannedDate`).
   * Projects have no standalone due-date field, so this is the single source of
   * truth used by both the project list and the project detail page.
   *
   * @param {string} projectId - ID of the project
   * @param {string} newDate - The new due date (any parseable date string)
   * @returns {Promise<WorkflowGenericResponse<WorkflowMilestoneUpdateResponseData>>} The updated milestone
   * @throws {NoMilestoneError} If the project has no dated milestone to anchor to
   * @throws {Error} If projectId is missing or the API request fails
   */
  static async setProjectDueDate(
    projectId: string,
    newDate: string
  ): Promise<WorkflowGenericResponse<WorkflowMilestoneUpdateResponseData>> {
    if (!projectId) {
      throw new Error('Project ID is required.');
    }

    const response = await MilestoneService.getProjectMilestones(projectId, {
      limit: 200,
      page: 1,
    });
    const dated = (response?.data?.milestones ?? []).filter((m) =>
      Boolean(m.plannedDate)
    );

    if (dated.length === 0) {
      throw new NoMilestoneError();
    }

    // Planned dates share the YYYY-MM-DD prefix, so a lexicographic compare
    // orders them chronologically and finds the milestone with the latest date.
    const target = dated.reduce((latest, m) =>
      (m.plannedDate as string) > (latest.plannedDate as string) ? m : latest
    );

    // Milestone dates are calendar dates; drop any time component.
    const plannedDate = /^\d{4}-\d{2}-\d{2}$/.test(newDate)
      ? newDate
      : newDate.slice(0, 10);

    return MilestoneService.updateMilestone(projectId, target.milestoneId, {
      plannedDate,
    });
  }

  /**
   * Retrieves audit log entries for a milestone.
   *
   * @param {string} projectId - ID of the project
   * @param {string} milestoneId - ID of the milestone
   * @param {number} page - Page number (optional, default: 1)
   * @param {number} limit - Items per page (optional, default: 50)
   * @param {string} sortOrder - Sort order 'asc' or 'desc' (optional, default: 'desc')
   * @returns {Promise<WorkflowGenericResponse<MilestoneAuditResponseData>>} Audit log entries with pagination
   * @throws {Error} If the API request fails
   */
  static async getMilestoneAudit(
    projectId: string,
    milestoneId: string,
    page?: number,
    limit?: number,
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<WorkflowGenericResponse<MilestoneAuditResponseData>> {
    if (!projectId) {
      throw new Error('Project ID is required.');
    }
    if (!milestoneId) {
      throw new Error('Milestone ID is required.');
    }

    const queryParams: Record<string, string | number> = {};
    if (page != null) {
      queryParams.page = page;
    }
    if (limit != null) {
      queryParams.limit = limit;
    }
    if (sortOrder) {
      queryParams.sortOrder = sortOrder;
    }

    return http.get<WorkflowGenericResponse<MilestoneAuditResponseData>>(
      `${pathProjectMilestoneId(projectId, milestoneId)}/audit`,
      {
        params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      }
    );
  }

  /**
   * Exports audit log as CSV for a milestone.
   * Opens the CSV export endpoint in a new window to trigger download.
   *
   * @param {string} projectId - ID of the project
   * @param {string} milestoneId - ID of the milestone
   * @returns {Promise<void>} Downloads CSV file
   * @throws {Error} If the API request fails
   */
  static async exportMilestoneAuditCSV(
    projectId: string,
    milestoneId: string
  ): Promise<void> {
    if (!projectId) {
      throw new Error('Project ID is required.');
    }
    if (!milestoneId) {
      throw new Error('Milestone ID is required.');
    }

    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL || '';
      const url = `${baseURL}${pathProjectMilestoneId(projectId, milestoneId)}/audit?format=csv`;
      
      // Open in new window to trigger download
      // The backend returns CSV with Content-Disposition header
      window.open(url, '_blank');
    } catch (error: any) {
      throw new Error(`Failed to export audit log: ${error?.message || 'Unknown error'}`);
    }
  }

  /**
   * Creates a new ad-hoc milestone for a project.
   *
   * @param {string} projectId - ID of the project
   * @param {CreateMilestonePayload} payload - Milestone data
   * @returns {Promise<WorkflowGenericResponse<CreateMilestoneResponseData>>} Created milestone data
   * @throws {Error} If the API request fails
   */
  static async createMilestone(
    projectId: string,
    payload: CreateMilestonePayload
  ): Promise<WorkflowGenericResponse<CreateMilestoneResponseData>> {
    if (!projectId) {
      throw new Error('Project ID is required.');
    }
    if (!payload.name || !payload.name.trim()) {
      throw new Error('Milestone name is required.');
    }

    return http.post<
      WorkflowGenericResponse<CreateMilestoneResponseData>,
      CreateMilestonePayload
    >(pathProjectMilestones(projectId), payload);
  }
  /**
   * Replaces all milestones on a project by applying a different workflow template.
   * Archives existing instances, deletes old milestones, and applies the new template atomically.
   *
   * @param {string} projectId - ID of the project / matter
   * @param {object} payload - Replacement data
   * @param {string} payload.templateId - ID of the new workflow template
   * @param {string} payload.anchorDate - Anchor date for calculating milestone dates (YYYY-MM-DD)
   * @param {string} [payload.anchorField] - Anchor field type (default: projectKickoffDate)
   * @param {Record<string, string>} [payload.projectAnchorDates] - Additional anchor dates
   * @returns {Promise<WorkflowGenericResponse<any>>} New milestones and instance data
   * @throws {Error} If the API request fails
   */
  static async replaceProjectWorkflow(
    projectId: string,
    payload: {
      templateId: string;
      anchorDate: string;
      anchorField?: string;
      projectAnchorDates?: Record<string, string>;
    }
  ): Promise<WorkflowGenericResponse<any>> {
    if (!projectId) {
      throw new Error('Project ID is required.');
    }
    if (!payload.templateId || !payload.anchorDate) {
      throw new Error('templateId and anchorDate are required.');
    }

    return http.post<WorkflowGenericResponse<any>, typeof payload>(
      `${pathProjectMilestones(projectId)}/replace-workflow`,
      payload
    );
  }
}

export default MilestoneService;

